
import { defineComponent, onMounted, ref, reactive } from "vue";
import { ErrorMessage, Field, Form } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import Swal from "sweetalert2/dist/sweetalert2.js";
import * as Yup from "yup";
import mAxiosApi from "@/api";
import { useI18n } from "vue-i18n";
import { ElNotification } from 'element-plus'
import { useStore } from "vuex";
import store from "@/store";
import { Mutations } from "@/store/enums/StoreEnums";
import ContactNotifPicker from "@/components/still/ContactNotifPicker.vue";

interface ProfileDetails {
  con_prenom: string;
  con_nom: string;
  con_role: string;
  con_tel1: string;
  con_tel2: string;
  con_ccoid: string;
  con_meraki_key: string,
  con_mail: string;
  con_newmail: string;
  con_password: string;
  con_password_new: string;
  con_password_new_confirm: string;
  notifs: string[];
}

export default defineComponent({
  name: "account-settings",
  components: {
    ErrorMessage,
    Field,
    Form,
    ContactNotifPicker,
  },
  setup() {
    const { t } = useI18n()
    const submitButton1 = ref<HTMLElement | null>(null);
    const updateEmailButton = ref<HTMLElement | null>(null);
    const updatePasswordButton = ref<HTMLElement | null>(null);

    const emailFormDisplay = ref(false);
    const passwordFormDisplay = ref(false);

    const mStore = useStore();

    const state = reactive({
      role : mStore.getters.currentUser.con_role_web,
    });

    const profileDetailsValidator = Yup.object().shape({
      con_prenom: Yup.string().nullable().required(t("Le prénom est obligatoire")),
      con_nom: Yup.string().nullable().required(t("Le nom est obligatoire")),
      con_role: Yup.string().nullable().required(t("La fonction est obligatoire")),
      con_tel1: Yup.string().nullable().label(t("Télephone Fixe")),
      con_tel2: Yup.string().nullable().label(t("Télephone Portable")),
      con_ccoid: Yup.string().nullable().label(t("CCO ID")),
      con_meraki_key: Yup.string().nullable().label(t("Clé MERAKI")),
    });

    const changeEmail = Yup.object().shape({
      emailaddress: Yup.string().required().email().label(t("Email")),
      confirmemailpassword: Yup.string().required().label(t("Password")),
    });

    const changePassword = Yup.object().shape({
      currentpassword: Yup.string()
        .required(t("Mot de passe actuel obligatoire"))
        .label(t("Current password")),
      newpassword: Yup.string()
        .min(8)
        .required(t("Mot de passe actuel obligatoire"))
        .label(t("Password")),
      confirmpassword: Yup.string()
        .min(8)
        .required(t("Confirmation du nouveau Mot de passe obligatoire"))
        .oneOf(
          [Yup.ref("newpassword"), null],
          t("Les nouveaux mots des passes doivent être identiques")
        )
        .label(t("Password Confirmation")),
    });

    const profileDetails = ref<ProfileDetails>({
      con_prenom: "",
      con_nom: "",
      con_role: "",
      con_tel1: "",
      con_tel2: "",
      con_ccoid: "",
      con_meraki_key : "",
      con_mail: "",
      con_newmail: "",
      con_password: "",
      con_password_new: "",
      con_password_new_confirm: "",
      notifs: [] as string[],
    });

    const testAPiMeraki = () => {
      mAxiosApi
          .get("testAPIMeraki/" + profileDetails.value.con_meraki_key)
          .then(({ data }) => {
            if(data.results.length > 0) {
              console.log(data);
              ElNotification({
                title: 'Succès',
                message: 'Votre clé semble valide',
                type: 'success',
              })
            }else {
              ElNotification({
                title: 'Erreur',
                message: 'Votre clé ne semble pas valide',
                type: 'error',
              }) 
            }
          })
          .catch((err) => {
            console.error(err);
          });
    }

    const saveChanges1 = () => {
      if (submitButton1.value) {
        // Activate indicator
        submitButton1.value.setAttribute("data-kt-indicator", "on");

        mAxiosApi
          .post("updateProfil", { _value: profileDetails.value })
          .then(({ data }) => {
            // console.log(data["results"]);
            submitButton1.value?.removeAttribute("data-kt-indicator");

            store.commit(Mutations.SET_USER, data["results"]);

            Swal.fire({
              text: t("Changement de vos informations personnelles correctement effectué !"),
              icon: "success",
              confirmButtonText: t("Ok"),
              buttonsStyling: false,
              customClass: {
                confirmButton: "btn btn-light-primary",
              },
            });
          })
          .catch((err) => {
            console.error(err);
          });
      }
    };

    const updateEmail = () => {
      if (updateEmailButton.value) {
        // Activate indicator
        updateEmailButton.value.setAttribute("data-kt-indicator", "on");

        mAxiosApi
          .post("updateMail", profileDetails)
          .then(({ data }) => {
            // console.log(data);
            updateEmailButton.value?.removeAttribute("data-kt-indicator");

            if (data["results"] !== 0) {
              store.commit(Mutations.SET_USER, data["results"]);
              emailFormDisplay.value = false;
              profileDetails.value.con_mail = profileDetails.value.con_newmail;
              Swal.fire({
                text: t("Changement de votre Email correctement effectué !"),
                icon: "success",
                confirmButtonText: t("Ok"),
                buttonsStyling: false,
                customClass: {
                  confirmButton: "btn btn-light-primary",
                },
              });
            } else {
              Swal.fire({
                text: t("Erreur lors du chagement de mail. Veuillez vérifier votre adresse Email et votre mot de passe, svp."),
                icon: "error",
                confirmButtonText: t("Ok"),
                buttonsStyling: false,
                customClass: {
                  confirmButton: "btn btn-light-primary",
                },
              });
            }
          })
          .catch((err) => {
            console.error(err);
          });
      }
    };

    const updatePassword = () => {
      if (updatePasswordButton.value) {
        // Activate indicator
        updatePasswordButton.value.setAttribute("data-kt-indicator", "on");

        mAxiosApi
          .post("password", {
            username: profileDetails.value.con_mail,
            password: profileDetails.value.con_password,
            newPassword: profileDetails.value.con_password_new,
          })
          .then(({ data }) => {
            // console.log(data);
            updatePasswordButton.value?.removeAttribute("data-kt-indicator");
            passwordFormDisplay.value = false;
            Swal.fire({
              text: t("Changement de votre mot de passe correctement effectué !"),
              icon: "success",
              confirmButtonText: t("Ok"),
              buttonsStyling: false,
              customClass: {
                confirmButton: "btn btn-light-primary",
              },
            });
          })
          .catch(() => {
            updatePasswordButton.value?.removeAttribute("data-kt-indicator");
            Swal.fire({
              text: t("Erreur lors du chagement de mot de passe. Veuillez vérifier vos informations et réessayer, svp."),
              icon: "error",
              confirmButtonText: t("Ok"),
              buttonsStyling: false,
              customClass: {
                confirmButton: "btn btn-light-primary",
              },
            });
          });
      }
    };

    onMounted(async () => {
      setCurrentPageBreadcrumbs("Paramètres du compte", [""]);
      let meAndMyself = await mAxiosApi.prototype.getAxios("/me");
      // console.log(meAndMyself);

      profileDetails.value.con_prenom = meAndMyself.con_prenom;
      profileDetails.value.con_nom = meAndMyself.con_nom;
      profileDetails.value.con_role = meAndMyself.con_role;
      profileDetails.value.con_tel1 = meAndMyself.con_tel1;
      profileDetails.value.con_tel2 = meAndMyself.con_tel2;
      profileDetails.value.con_ccoid = meAndMyself.con_ccoid;
      profileDetails.value.con_meraki_key = meAndMyself.con_meraki_key;
      profileDetails.value.con_mail = meAndMyself.con_mail;
      profileDetails.value.con_newmail = meAndMyself.con_mail;
      profileDetails.value.con_password = "";
      profileDetails.value.con_password_new = "";
      profileDetails.value.con_password_new_confirm = "";
      profileDetails.value.notifs = [];

      try {
        const contactsRes = await mAxiosApi.prototype.getAxios("/getContacts");
        const meContact = (contactsRes.results || []).find(
          (c) => Number(c.con_seq) === Number(meAndMyself.con_seq)
        );
        if (meContact?.notifs_actifs?.length) {
          profileDetails.value.notifs = meContact.notifs_actifs.map(
            (n) => n.tnc_code
          );
        }
      } catch (err) {
        console.error(err);
      }
    });

    return {
      submitButton1,
      saveChanges1,
      profileDetails,
      emailFormDisplay,
      passwordFormDisplay,
      profileDetailsValidator,
      changeEmail,
      changePassword,
      updateEmailButton,
      updatePasswordButton,
      updateEmail,
      updatePassword,
      testAPiMeraki,
      state
    };
  },
});
